import { ACCESSIBILITY_FINAL_2026_09_13, PRIVACY_FINAL_2026_09_21, TERMS_FINAL_2026_09_21, } from './bodies/index.js';
/**
 * The only body a placeholder version may carry. Counsel's working draft lives
 * outside the repo — registration proceeds under the placeholder until the
 * first published version flips the document to its real text.
 */
export const PLACEHOLDER_LEGAL_NOTICE = 'This document is being prepared and will be posted here. Registration proceeds under the placeholder; you will be asked to accept the final text when it is published.';
const SEED_EFFECTIVE_DATE = '2026-01-01T00:00:00.000Z';
function placeholderVersion(documentId) {
    return {
        id: `${documentId}-v1`,
        version: 1,
        effectiveDate: SEED_EFFECTIVE_DATE,
        // A placeholder's text is posted the moment it governs — the two facts
        // are only distinct once a real version is appended ahead of its
        // effective date.
        publishedAt: SEED_EFFECTIVE_DATE,
        material: false,
        status: 'placeholder',
        body: PLACEHOLDER_LEGAL_NOTICE,
    };
}
// The acceptance model each document id requires. The registry declares it on
// the document; this table is what `validateLegalRegistry` holds that
// declaration to, so a document cannot silently adopt another id's model.
// Exported as the single authority a hand-built document reads to declare the
// model its id requires, rather than restating the mapping at each build site.
export const REQUIRED_ACCEPTANCE_MODEL_BY_ID = {
    terms: 'notice-and-continued-use',
    privacy: 'publication',
    accessibility: 'informational',
};
// The only source of truth for what the portal renders and for which version
// is current. Every document seeds one placeholder version with a past
// effective date, so a current version exists for registration to accept;
// `material: false` is legal on a placeholder — R12 constrains only the first
// PUBLISHED version. Swapping in real text is appending one
// `{ status: 'published', material: true }` version object to the list, which
// each document below now carries: counsel's text is never typed here, it is
// the `.body` of the generated module the package commits under `bodies/`.
export const LEGAL_DOCUMENTS = [
    {
        id: 'terms',
        title: 'Terms of Service',
        acceptanceModel: 'notice-and-continued-use',
        versions: [
            placeholderVersion('terms'),
            {
                id: 'terms-v2',
                version: 2,
                // A full notice period apart: `validateLegalRegistry` refuses a
                // material Terms version whose text is posted less than fourteen days
                // before it governs.
                effectiveDate: '2026-10-12T00:00:00.000Z',
                publishedAt: '2026-09-23T00:00:00.000Z',
                material: true,
                status: 'published',
                body: TERMS_FINAL_2026_09_21.body,
            },
        ],
    },
    {
        id: 'privacy',
        title: 'Privacy Policy',
        acceptanceModel: 'publication',
        versions: [
            placeholderVersion('privacy'),
            {
                id: 'privacy-v2',
                version: 2,
                effectiveDate: '2026-10-12T00:00:00.000Z',
                publishedAt: '2026-09-23T00:00:00.000Z',
                material: true,
                status: 'published',
                body: PRIVACY_FINAL_2026_09_21.body,
            },
        ],
    },
    {
        id: 'accessibility',
        title: 'Accessibility Statement',
        acceptanceModel: 'informational',
        versions: [
            placeholderVersion('accessibility'),
            {
                id: 'accessibility-v2',
                version: 2,
                // Posted at the instant it governs: the document never has a notice
                // gap, so it never announces and never produces a notice.
                effectiveDate: '2026-09-23T00:00:00.000Z',
                publishedAt: '2026-09-23T00:00:00.000Z',
                material: true,
                status: 'published',
                body: ACCESSIBILITY_FINAL_2026_09_13.body,
            },
        ],
    },
];
/**
 * Lift a UTC instant to the fixed `YYYY-MM-DDTHH:mm:ss.sssZ` shape the
 * schema pins, so a second-precision value (`...:00Z`) compares correctly
 * against a millisecond one instead of sorting first on an effective-date tie.
 * Values already at millisecond precision (the only shape `LegalInstantSchema`
 * admits) pass through unchanged.
 */
export function normalizeLegalInstant(instant) {
    return instant.length === 20 && instant.endsWith('Z') ? `${instant.slice(0, 19)}.000Z` : instant;
}
/**
 * Whether a version's text has been POSTED at `now` — its posting instant has
 * arrived. The one statement of that fact: the notice predicate reads it to
 * decide what may be announced, and the re-acceptance write reads it to decide
 * what text a caller can honestly be said to have accepted. Reading the posting
 * instant and never the effective date is deliberate — a version posted before
 * it governs is exactly the notice case (see {@link termsNoticeVerdictFor}).
 * Both sides are normalized by {@link normalizeLegalInstant} before the string
 * compare, mirroring {@link currentVersionOf}.
 */
export function isPostedLegalVersion(version, now) {
    return normalizeLegalInstant(version.publishedAt) <= normalizeLegalInstant(now);
}
/**
 * The declared version numbered `version` of document `id` when its text has
 * been posted at `now`, or undefined when the document declares no such version
 * or its text has not been posted yet. A version number the document does not
 * declare is a lookup miss rather than an eligibility answer, which is what
 * lets a write gate refuse a number the registry never published.
 */
export function postedLegalVersionOf(documents, id, version, now) {
    const document = documents.find((entry) => entry.id === id);
    if (!document)
        return undefined;
    const declared = document.versions.find((entry) => entry.version === version);
    return declared && isPostedLegalVersion(declared, now) ? declared : undefined;
}
/**
 * The version of `id` the given registry serves at `now`: the highest-numbered
 * version whose `effectiveDate` is at or before `now`, or undefined when no
 * version is effective yet. `now` is always caller-supplied — nothing in this
 * module reads a clock. Effective dates are compared as normalized
 * `YYYY-MM-DDTHH:mm:ss.sssZ` strings (see {@link normalizeLegalInstant}), so
 * the at-or-before comparison stays the plain string compare the schema's
 * fixed precision guarantees.
 *
 * Selection is by effective date alone. A version's posting instant is a
 * separate fact and deliberately plays no part here — see
 * {@link termsNoticeVerdictFor}, which reads it.
 */
export function currentVersionOf(documents, id, now) {
    const document = documents.find((entry) => entry.id === id);
    if (!document)
        return undefined;
    const atOrBefore = normalizeLegalInstant(now);
    let current;
    for (const version of document.versions) {
        if (normalizeLegalInstant(version.effectiveDate) <= atOrBefore &&
            (current === undefined || version.version > current.version)) {
            current = version;
        }
    }
    return current;
}
export function currentLegalVersion(id, now) {
    return currentVersionOf(LEGAL_DOCUMENTS, id, now);
}
/**
 * The next version number for document `id` — one above the highest number the
 * document declares, at any status, or 1 when it declares none. The one home of
 * the no-collision policy: a version staged AHEAD of the registry (a synthetic
 * one a suite or an api-lane case stands up to read back, never one counsel's
 * text ships) must not collide with a version a later real append reaches — the
 * document would then declare the number twice, which
 * {@link validateLegalRegistry} reports as `duplicate-version-number` — and must
 * not sort below a version already declared. Derived from the documents rather
 * than restated at each staging site, so the number moves with the registry
 * instead of having to be found and edited on the far side of an append.
 *
 * Throws for a registry that carries no such document: staging a version of a
 * document the bundle does not declare is a caller error, not a number. A plain
 * `Error` rather than an assertion — this module is bundled into the portal SPA
 * and compiled by the Docker build without node's test types, so it carries no
 * node import.
 */
export function nextVersionFor(documents, id) {
    const document = documents.find((entry) => entry.id === id);
    if (!document)
        throw new Error(`the registry carries no ${id} document`);
    let highest = 0;
    for (const version of document.versions) {
        if (version.version > highest)
            highest = version.version;
    }
    return highest + 1;
}
/**
 * The version of `id` announced at `now` but not yet governing: the
 * highest-numbered version whose text {@link isPostedLegalVersion} reports
 * posted at `now`, whose `effectiveDate` is strictly after `now`, and whose
 * number stands above the version {@link currentVersionOf} answers for that same
 * `now` — or undefined when the document declares no such version. That
 * undefined is the ordinary answer, not an error: it is what the banner, the
 * upcoming address and the notice all read as "nothing to announce".
 *
 * Strictly-after is deliberate and is the one point where this selector and
 * {@link currentVersionOf} disagree: at the effective instant itself the version
 * governs, so it is current rather than upcoming. Reusing that selector for the
 * lower bound keeps in force stated once, so the two answers cannot drift apart.
 *
 * `now` is always caller-supplied — nothing in this module reads a clock. Both
 * sides of the effective-date compare are normalized by
 * {@link normalizeLegalInstant} exactly as {@link currentVersionOf} does, so a
 * second-precision instant cannot sort wrong on a tie. Posting eligibility is
 * {@link isPostedLegalVersion}'s alone; this selector adds no status or
 * materiality filter of its own, because the glossary's upcoming means posted,
 * not yet governing, and numbered above in force — nothing more — and each
 * consuming surface owns its own eligibility gate.
 */
export function upcomingVersionOf(documents, id, now) {
    const document = documents.find((entry) => entry.id === id);
    if (!document)
        return undefined;
    const atOrBefore = normalizeLegalInstant(now);
    // Reused rather than re-derived: "numbered above the in-force version" is the
    // same question `currentVersionOf` already answers, and restating its scan
    // here would be a second statement of when a version governs. Undefined means
    // nothing is effective yet, so a posted future-effective version still stands
    // above — there is no lower bound to apply.
    const current = currentVersionOf(documents, id, now);
    let upcoming;
    for (const version of document.versions) {
        if (!isPostedLegalVersion(version, now))
            continue;
        if (normalizeLegalInstant(version.effectiveDate) <= atOrBefore)
            continue;
        if (current !== undefined && version.version <= current.version)
            continue;
        if (upcoming === undefined || version.version > upcoming.version)
            upcoming = version;
    }
    return upcoming;
}
export function upcomingLegalVersion(id, now) {
    return upcomingVersionOf(LEGAL_DOCUMENTS, id, now);
}
/**
 * The presentation the announced version's standing earns at `now` — the one
 * decision point behind both the notice's link target and its wording, so the
 * two cannot describe different versions.
 *
 * `version` is the version the notice announces (the verdict's own `version`,
 * which is `null` when no notice is due). While {@link upcomingVersionOf}
 * answers that same version, the notice leads to the announced text's own
 * upcoming address and says the change is pending; once the version governs,
 * it leads to the document's address and may say the text has been updated.
 * That one question is asked rather than re-derived, so "posted and not yet
 * governing" stays stated once and the notice's answer cannot drift from the
 * upcoming address's.
 *
 * Two inputs take the in-force answer, and both are the conservative case —
 * the arm that promises nothing the surface cannot address: a `null` version,
 * because no notice is due to present at all, and a version the registry does
 * not declare, which is the ordinary deploy skew of a portal bundle built
 * against a registry older than the one the acceptance API answered from. A
 * number the registry cannot place is never evidence that a change is pending.
 *
 * `now` is always caller-supplied — nothing in this module reads a clock.
 */
export function termsNoticePresentationFor(documents, version, now) {
    const inForce = { linkTarget: 'in-force', wording: 'change-in-force' };
    if (version === null)
        return inForce;
    const document = documents.find((entry) => entry.id === 'terms');
    if (!document)
        return inForce;
    if (!document.versions.some((entry) => entry.version === version))
        return inForce;
    const upcoming = upcomingVersionOf(documents, 'terms', now);
    return upcoming !== undefined && upcoming.version === version
        ? { linkTarget: 'upcoming', wording: 'pending-change' }
        : inForce;
}
/** No notice is due, with both announced fields explicitly null. */
function noTermsNotice() {
    return { due: false, version: null, effectiveDate: null };
}
/**
 * The Terms notice verdict for one account — the single function both the
 * acceptance-state API (which decides and answers `GET /api/legal/acceptance`)
 * and the portal (which renders the dismissible notice) read, so the two
 * cannot disagree. A notice is due exactly when the account is a customer, a
 * MATERIAL PUBLISHED Terms version stands above its recorded acceptance, that
 * version's text has been POSTED (`publishedAt <= now`), and no dismissal at
 * or above that version is recorded — and never otherwise; in particular
 * neither privacy nor accessibility is ever consulted, so no input makes this
 * return one of their versions.
 *
 * Eligibility reads the posting instant and never the effective date: a
 * version posted before it governs is exactly the notice case, and the verdict
 * reports that version's still-future `effectiveDate` for the notice to
 * announce. The arrival test is {@link isPostedLegalVersion}, the one statement
 * of that fact the write gates read too. `now` is always caller-supplied —
 * nothing in this module reads a clock.
 */
export function termsNoticeVerdictFor(documents, account, now) {
    if (account.isPlatformUser)
        return noTermsNotice();
    const document = documents.find((entry) => entry.id === 'terms');
    if (!document)
        return noTermsNotice();
    const accepted = account.acceptedTermsVersion ?? 0;
    let standing;
    for (const version of document.versions) {
        if (
        // `status === 'published'` is load-bearing, not redundant with the
        // materiality flag: a placeholder may legally be flagged material, and
        // a placeholder-era version must never be served as a notice.
        version.status === 'published' &&
            version.material &&
            isPostedLegalVersion(version, now) &&
            version.version > accepted &&
            (standing === undefined || version.version > standing.version)) {
            standing = version;
        }
    }
    if (!standing)
        return noTermsNotice();
    if (account.dismissedTermsVersion !== null && account.dismissedTermsVersion >= standing.version) {
        return noTermsNotice();
    }
    return { due: true, version: standing.version, effectiveDate: standing.effectiveDate };
}
export function termsNoticeVerdict(account, now) {
    return termsNoticeVerdictFor(LEGAL_DOCUMENTS, account, now);
}
// The notice period a material Terms version owes between the instant its text
// is posted and the instant it governs: fourteen days, the same period the
// notice's own copy promises. Declared once, here, because the validator is the
// only place the registry states the rule — a consuming surface that needs the
// number reads meaning off `notice-gap-too-short` rather than restating it.
const TERMS_NOTICE_PERIOD_MS = 14 * 24 * 60 * 60 * 1000;
/**
 * Registry invariants, reported as violations rather than thrown so callers
 * (and the property tests) can assert on the exact code:
 * - every document declares the acceptance model its id requires — the model
 *   is what a version bump of that document means, so a document adopting
 *   another id's model would make its bumps mean the wrong thing everywhere;
 * - every document declares at least one version — a structurally-empty
 *   document has no current version, so it silently serves and records
 *   nothing;
 * - the first published version of every document — the version that flips it
 *   from placeholder to its real text — must be flagged material, so every
 *   account that registered against the placeholder is asked to accept the
 *   real text once;
 * - every placeholder version must carry the placeholder notice byte-exactly
 *   (no trim, no prefix) — counsel's text is never authored in the registry;
 * - a material published Terms version must be posted at least
 *   {@link TERMS_NOTICE_PERIOD_MS} before it governs — continued use is only
 *   meaningful notice if the text was on the page for the promised period, and
 *   a version that governs the day it appears was never announced. The rule is
 *   Terms-only and published-material-only: privacy and accessibility consent
 *   to nothing by continued use, and a placeholder has no text to announce;
 * - version numbers are unique per document, which is what makes the
 *   integer-monotonic current-version comparison well-defined.
 */
export function validateLegalRegistry(documents = LEGAL_DOCUMENTS) {
    const violations = [];
    for (const document of documents) {
        const requiredModel = REQUIRED_ACCEPTANCE_MODEL_BY_ID[document.id];
        if (document.acceptanceModel !== requiredModel) {
            violations.push({
                documentId: document.id,
                code: 'acceptance-model-mismatch',
                message: `Document ${document.id} declares acceptance model ${document.acceptanceModel}; its id requires ${requiredModel}.`,
            });
        }
        if (document.versions.length === 0) {
            violations.push({
                documentId: document.id,
                code: 'no-versions',
                message: `Document ${document.id} declares no versions; there is no current version to serve or record acceptance against.`,
            });
            continue;
        }
        const seenVersions = new Set();
        let firstPublished;
        for (const version of document.versions) {
            if (seenVersions.has(version.version)) {
                violations.push({
                    documentId: document.id,
                    versionId: version.id,
                    code: 'duplicate-version-number',
                    message: `Document ${document.id} declares version ${version.version} more than once; the per-document version ordering needs unique numbers.`,
                });
            }
            seenVersions.add(version.version);
            if (version.status === 'published' &&
                (firstPublished === undefined || version.version < firstPublished.version)) {
                firstPublished = version;
            }
            if (version.status === 'placeholder' && version.body !== PLACEHOLDER_LEGAL_NOTICE) {
                violations.push({
                    documentId: document.id,
                    versionId: version.id,
                    code: 'placeholder-body-not-notice',
                    message: `Placeholder version ${version.id} of ${document.id} must carry the placeholder notice verbatim.`,
                });
            }
            // Elapsed milliseconds rather than a string compare: the two instants are
            // subtracted, not ordered, so the fixed-precision normalization the
            // string compares elsewhere need has nothing to buy here — two spellings
            // of the same moment parse to the same epoch value. Strictly-less-than is
            // the boundary the period's name states: a gap of exactly
            // TERMS_NOTICE_PERIOD_MS is the period served, not a day short of it.
            if (document.id === 'terms' &&
                version.status === 'published' &&
                version.material &&
                Date.parse(version.effectiveDate) - Date.parse(version.publishedAt) < TERMS_NOTICE_PERIOD_MS) {
                violations.push({
                    documentId: document.id,
                    versionId: version.id,
                    code: 'notice-gap-too-short',
                    message: `Terms version ${version.id} governs on ${version.effectiveDate} but its text is posted on ${version.publishedAt}; a material Terms version needs at least 14 days of notice between the two.`,
                });
            }
        }
        if (firstPublished && !firstPublished.material) {
            violations.push({
                documentId: document.id,
                versionId: firstPublished.id,
                code: 'first-published-not-material',
                message: `The first published version of ${document.id} must be flagged material so placeholder-era accounts are asked once for the real text.`,
            });
        }
    }
    return violations;
}
